import { Check, ChevronDown, Globe2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLanguage } from '../../i18n/LanguageContext'
import { languageOptions, type Language } from '../../i18n/translations'

export function LanguageSwitcher({ mobile = false }: { mobile?: boolean }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const { language, setLanguage, t } = useLanguage()
  const current = languageOptions.find((option) => option.code === language) ?? languageOptions[0]

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [])

  const select = (code: Language) => {
    setLanguage(code)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className={`relative ${mobile ? 'w-full' : ''}`} onKeyDown={(event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        rootRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
      }
    }}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-label={t('Select language')} aria-haspopup="listbox" aria-expanded={open} className={`flex items-center rounded-lg border border-white/[.08] text-sm font-medium text-zinc-400 transition duration-200 hover:border-white/[.15] hover:bg-white/[.035] hover:text-white ${mobile ? 'h-11 w-full justify-between px-3' : 'h-10 gap-2 px-3'}`}>
        <span className="flex items-center gap-2"><Globe2 size={16} strokeWidth={1.8} aria-hidden="true" className="text-brand" /><span>{current.short}</span>{mobile && <span className="text-xs font-normal text-zinc-600">{current.label}</span>}</span>
        <ChevronDown size={13} strokeWidth={1.8} aria-hidden="true" className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`${mobile ? 'relative mt-2 w-full' : 'absolute right-0 top-full z-50 mt-2 w-44'} overflow-hidden rounded-xl border border-white/10 bg-[#0d121a] p-1.5 shadow-[0_18px_60px_rgba(0,0,0,.55)]`} role="listbox" aria-label={t('Select language')}>
          {languageOptions.map((option) => (
            <button key={option.code} type="button" role="option" data-language={option.code} aria-selected={language === option.code} onClick={() => select(option.code)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition ${language === option.code ? 'bg-brand/[.08] text-white' : 'text-zinc-400 hover:bg-white/[.05] hover:text-white'}`}>
              <span className="w-6 text-[10px] font-semibold tracking-wider text-brand">{option.short}</span><span className="flex-1">{option.label}</span>{language === option.code && <Check size={14} strokeWidth={2} aria-hidden="true" className="text-brand" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
